import { ChevronRight, CircleHelp, Ellipsis, PanelLeftClose, PanelLeftOpen } from '@orgus/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Pressable } from '@orgus/ui';
import { isActive, MORE_NAV, NAV, SETTINGS_NAV } from '../../lib/nav.ts';
import type { NavItem } from '../../lib/nav.ts';
import { usePrefs } from '../../lib/prefs.ts';
import { useRouteTarget } from '../../lib/routeSwitch.ts';
import { useCounts } from '../../lib/store.ts';
import { COMPACT_RAIL_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { IconButton, OrgusLockup } from '@orgus/ui';
import { AppearanceMenu } from './AppearanceMenu.tsx';
import { useRailSheet } from './railSheet.ts';
import { Tooltip } from '@orgus/ui';
import { useWaitingOnYou } from '../../features/approvals/waiting.ts';

/**
 * The Inbox badge (ADR 0056), a neutral count with a dot: red once a request has stopped a session (R-HL-1), amber while
 * anything else is blocked, no dot when only asked questions wait. `pending` is the blocked rest.
 */
export function inboxBadge(pending: number, blocked: number, asked = 0): { n: number; tone: 'waiting' | 'failed' | 'neutral'; label: string } | undefined {
  const n = pending + blocked + asked;
  if (!n) return undefined;
  const label = [blocked && `${blocked} blocked`, pending && `${pending} waiting for you`, asked && `${asked} asked`].filter(Boolean).join(', ');
  return { n, tone: blocked ? 'failed' : pending ? 'waiting' : 'neutral', label };
}

function useNavCounts(): Record<string, { n: number; tone: 'waiting' | 'neutral' | 'failed'; label: string } | undefined> {
  const c = useCounts();
  const w = useWaitingOnYou();
  const assistant = w.assistant;
  // Sessions that wait on you, as the Sessions page shows them: blocked amber, asked grey.
  const sessionsBlocked = new Set(w.needs.blocked.filter((i) => i.kind === 'request' || i.kind === 'session').map((i) => i.sessionId)).size;
  const sessionsAsked = w.asked.length;
  const sessionsWaiting = sessionsBlocked + sessionsAsked;
  return {
    '/inbox': inboxBadge(w.urgent - w.blocked, w.blocked, w.asked.length),
    '/assistant': assistant ? { n: assistant, tone: 'waiting', label: `${assistant} ${assistant === 1 ? 'step waits' : 'steps wait'} for approval` } : undefined,
    '/sessions': sessionsWaiting
      ? { n: sessionsWaiting, tone: sessionsBlocked ? 'waiting' : 'neutral', label: [sessionsBlocked && `${sessionsBlocked} waiting for you`, sessionsAsked && `${sessionsAsked} asked`].filter(Boolean).join(', ') }
      : c.sessionsWorking
        ? { n: c.sessionsWorking, tone: 'neutral', label: `${c.sessionsWorking} working` }
        : undefined,
    '/agents': c.agentsRunning ? { n: c.agentsRunning, tone: 'neutral', label: `${c.agentsRunning} agents running` } : undefined,
    '/pipelines': c.pipelineInFlight
      ? { n: c.pipelineInFlight, tone: 'neutral', label: `${c.pipelineInFlight} ${c.pipelineInFlight === 1 ? 'change' : 'changes'} in flight: queued, verifying or deploying` }
      : undefined,
    '/machines':
      c.machinesTotal > c.machinesOnline
        ? { n: c.machinesTotal - c.machinesOnline, tone: 'failed', label: `${c.machinesTotal - c.machinesOnline} offline` }
        : undefined,
  };
}

/** One rail row: a link and the More toggle look the same. */
function railItemClass(collapsed: boolean, active: boolean): string {
  return cn(
    'group relative flex h-8 shrink-0 items-center gap-2 rounded-control text-ui font-medium -outline-offset-2 transition-colors duration-(--duration-fast)',
    collapsed ? 'w-8 justify-center' : 'w-full px-2',
    // Active: the Prism nav wash and its 2 px accent bar (colour.md, Act and mark); the label stays in fg.
    active ? 'prism-nav-active text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
  );
}
const railIconClass = (active: boolean) => cn('size-4 shrink-0', active ? 'text-accent-text' : 'text-fg-subtle group-hover:text-fg-muted');

/**
 * More (ADR 0076): the pages opened now and then, folded at the foot of the rail. Expanded it
 * is a disclosure that remembers being open and opens itself on one of its pages; collapsed it
 * is one icon with a menu beside it.
 */
function RailMore({ collapsed }: { collapsed: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const open = usePrefs((s) => s.railMore);
  const setOpen = usePrefs((s) => s.setRailMore);
  const here = MORE_NAV.find((i) => isActive(i, pathname));
  useEffect(() => {
    if (here && !usePrefs.getState().railMore) usePrefs.getState().setRailMore(true);
  }, [here]);

  if (collapsed) {
    return (
      <DropdownMenu>
        <Tooltip content={here ? `More: ${here.label}` : 'More'} side="right">
          <DropdownMenuTrigger asChild>
            <Pressable aria-label={here ? `More, ${here.label} open` : 'More'} className={railItemClass(true, !!here)}>
              {here ? <here.icon className={railIconClass(true)} /> : <Ellipsis className={railIconClass(false)} />}
            </Pressable>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent side="right" align="start" className="w-56">
          {MORE_NAV.map((item) => (
            <DropdownMenuItem key={item.to} icon={<item.icon />} onSelect={() => navigate(item.to)}>
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Expanded, the folded pages hang under the header on a guide line, indented and quieter than
  // the daily destinations above, so they read as the overflow they are.
  return (
    <>
      <Pressable aria-expanded={open} aria-controls="rail-more" onClick={() => setOpen(!open)} className={cn(railItemClass(false, false), 'mt-3')}>
        <Ellipsis className={railIconClass(false)} />
        <span className="min-w-0 flex-1 truncate">More</span>
        {!open && <span className="text-caption text-fg-subtle num">{MORE_NAV.length}</span>}
        <ChevronRight aria-hidden className={cn('size-3.5 shrink-0 text-fg-subtle transition-transform duration-(--duration-fast)', open && 'rotate-90')} />
      </Pressable>
      {open && (
        <div id="rail-more" role="group" aria-label="More" className="ml-4 flex flex-col gap-1 border-l border-border-subtle pl-2">
          {MORE_NAV.map((item) => (
            <RailLink key={item.to} item={item} collapsed={false} nested />
          ))}
        </div>
      )}
    </>
  );
}

function RailLink({ item, collapsed, count, nested }: { item: NavItem; collapsed: boolean; count?: { n: number; tone: string; label: string }; nested?: boolean }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  // While a page opens, the item it belongs to is marked at once, so the click answers before
  // the page is ready (lib/routeSwitch.ts).
  const target = useRouteTarget();
  // Active is computed here, not by NavLink's className function: a Tooltip trigger (Radix Slot)
  // merges class names as strings, and a function there broke the badged items' styles.
  const active = isActive(item, target ?? pathname);
  return (
    <Tooltip
      content={count ? `${item.label}: ${count.label}` : collapsed ? item.label : null}
      shortcut={['g', item.key]}
      shortcutThen
      side="right"
      // Expanded, the tooltip says what a count badge counts; collapsed, it names the item too.
      enabled={collapsed || !!count}
    >
      <NavLink
        to={item.to}
        end={item.to === '/'}
        aria-label={collapsed ? item.label : undefined}
        className={cn(railItemClass(collapsed, active), nested && !active && 'font-normal text-fg-subtle')}
      >
        <Icon className={railIconClass(active)} />
        {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
        {count &&
          (collapsed ? (
            <span
              aria-hidden
              className={cn(
                'absolute right-1 top-1 size-1.5 rounded-pill',
                count.tone === 'waiting' ? 'bg-status-attention' : count.tone === 'failed' ? 'bg-status-danger' : 'bg-fg-subtle',
              )}
            />
          ) : (
            // A neutral count, as in the bell: the state shows only as a small dot beside the number.
            <span role="img" aria-label={count.label} className="inline-flex min-w-5 shrink-0 items-center justify-end gap-1 text-caption font-medium text-fg-subtle num">
              {(count.tone === 'waiting' || count.tone === 'failed') && (
                <span aria-hidden className={cn('size-1.5 shrink-0 rounded-pill', count.tone === 'failed' ? 'bg-status-danger-fill' : 'bg-status-attention-fill')} />
              )}
              {count.n}
            </span>
          ))}
      </NavLink>
    </Tooltip>
  );
}

/**
 * Settings, the first of the footer icons beside appearance and shortcuts: a link drawn as an
 * icon button, marked while any /settings page is open.
 */
function RailSettings({ tooltipSide }: { tooltipSide: 'top' | 'right' }) {
  const { pathname } = useLocation();
  const target = useRouteTarget();
  const active = isActive(SETTINGS_NAV, target ?? pathname);
  const Icon = SETTINGS_NAV.icon;
  return (
    <Tooltip content={SETTINGS_NAV.label} shortcut={['g', SETTINGS_NAV.key]} shortcutThen side={tooltipSide}>
      <NavLink
        to={SETTINGS_NAV.to}
        aria-label={SETTINGS_NAV.label}
        className={cn(
          'inline-flex size-control-sm shrink-0 items-center justify-center rounded-control -outline-offset-2 transition-colors duration-(--duration-fast)',
          active ? 'bg-active text-accent-text' : 'text-fg-subtle hover:bg-hover hover:text-fg',
        )}
      >
        <Icon className="size-3.5" />
      </NavLink>
    </Tooltip>
  );
}

/**
 * The left rail: brand, sections, and the theme and help controls. Collapses to icons.
 * On a phone-width screen it is always icons; the toggle opens the full rail as an overlay
 * sheet (closed by the backdrop, Esc or a navigation) and leaves the desktop preference alone.
 */
export function Rail() {
  const prefCollapsed = usePrefs((s) => s.railCollapsed);
  const toggleRail = usePrefs((s) => s.toggleRail);
  const narrow = useMediaQuery(COMPACT_RAIL_QUERY);
  const sheet = useRailSheet((s) => s.open);
  const setSheet = useRailSheet((s) => s.setOpen);
  const location = useLocation();

  useEffect(() => {
    if (!narrow) setSheet(false);
  }, [narrow, setSheet]);
  useEffect(() => setSheet(false), [location.pathname, setSheet]);
  useEffect(() => {
    if (!sheet) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSheet(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet, setSheet]);

  return (
    <>
      <RailBody collapsed={narrow || prefCollapsed} onToggle={narrow ? () => setSheet(true) : toggleRail} toggleLabel={narrow ? 'Open sidebar' : undefined} inTitleBar />
      <AnimatePresence>
        {narrow && sheet && (
          <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Sections">
            <motion.div
              className="absolute inset-0 bg-backdrop backdrop-blur-[2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSheet(false)}
              aria-hidden
            />
            <motion.div
              className="relative h-full shadow-overlay"
              initial={{ x: -24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -24, opacity: 0 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            >
              <RailBody collapsed={false} onToggle={() => setSheet(false)} toggleLabel="Close sidebar" />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

/** `inTitleBar`: in the installed desktop app the brand and the toggle live in the title bar (TitleBar.tsx), so this row steps aside. */
function RailBody({ collapsed, onToggle, toggleLabel, inTitleBar }: { collapsed: boolean; onToggle: () => void; toggleLabel?: string; inTitleBar?: boolean }) {
  const setHelpOpen = usePrefs((s) => s.setHelpOpen);
  const counts = useNavCounts();

  return (
    <nav
      aria-label="Sections"
      className={cn(
        'flex h-full shrink-0 flex-col bg-rail',
        collapsed ? 'w-(--layout-rail-width-collapsed) items-center' : 'w-(--layout-rail-width)',
      )}
    >
      <div className={cn('flex h-(--layout-topbar-height) shrink-0 items-center', inTitleBar && 'wco:hidden', collapsed ? 'justify-center' : 'gap-2 pl-3 pr-3')}>
        {!collapsed && (
          <NavLink
            to="/"
            aria-label="Orgus, go to Overview"
            className="mr-auto flex h-8 items-center rounded-control px-2 text-fg outline-offset-2"
          >
            <OrgusLockup layout="compact" decorative />
          </NavLink>
        )}
        <IconButton
          label={toggleLabel ?? (collapsed ? 'Expand sidebar' : 'Collapse sidebar')}
          icon={collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          shortcut={toggleLabel ? undefined : ['[']}
          tooltipSide={collapsed ? 'right' : 'bottom'}
          onClick={onToggle}
        />
      </div>

      <div className={cn('flex flex-1 flex-col gap-0.5 overflow-y-auto pt-3', collapsed ? 'items-center px-0' : 'px-3')}>
        {NAV.map((item) => (
          <Fragment key={item.to}>
            {item.section &&
              (collapsed ? (
                <span aria-hidden className="my-2 h-px w-4 shrink-0 bg-border" />
              ) : (
                <span className="px-2 pb-1 pt-5 text-caption font-medium text-fg-subtle">{item.section}</span>
              ))}
            <RailLink item={item} collapsed={collapsed} count={counts[item.to]} />
          </Fragment>
        ))}
        <RailMore collapsed={collapsed} />
      </div>

      <div className={cn('flex shrink-0 items-center gap-1 py-3', collapsed ? 'flex-col' : 'px-3')}>
        <RailSettings tooltipSide={collapsed ? 'right' : 'top'} />
        <AppearanceMenu side={collapsed ? 'right' : 'top'} />
        <IconButton
          label="Keyboard shortcuts"
          icon={<CircleHelp />}
          shortcut={['?']}
          tooltipSide={collapsed ? 'right' : 'top'}
          onClick={() => setHelpOpen(true)}
        />
      </div>
    </nav>
  );
}
